const CHUNK_LOAD_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /loading chunk [\w-]+ failed/i,
  /\bchunkloaderror\b/i,
  /unable to preload css/i,
];
const NUXT_ASSET_PATTERN =
  /\/_nuxt\/[^\s"'<>)]*\.(?:js|mjs|css)(?:[?#][^\s"'<>)]*)?/i;
const RECOVERY_STORAGE_KEY = 'cleverdocs:nuxt-chunk-load-reload-at';
const DEFAULT_RECOVERY_COOLDOWN_MS = 60_000;

type RecoveryOptions = {
  cooldownMs?: number;
  location?: Location;
  navigator?: Navigator;
  now?: () => number;
  reload?: () => void;
  schedule?: (callback: () => void) => unknown;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
};

const EVIDENCE_KEYS = new Set([
  'error',
  'file',
  'filename',
  'message',
  'reason',
  'src',
  'stack',
  'to',
  'url',
]);

let reloadScheduled = false;

const collectEvidence = (
  value: unknown,
  parts: string[],
  seen: Set<object>,
  depth = 0,
): void => {
  if (depth > 5 || value === null || value === undefined) return;

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    parts.push(String(value));
    return;
  }

  if (typeof value !== 'object' || seen.has(value)) return;

  seen.add(value);

  if (value instanceof Error) {
    parts.push(value.name, value.message, value.stack ?? '');
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectEvidence(item, parts, seen, depth + 1));
    return;
  }

  for (const [key, item] of Object.entries(value)) {
    if (EVIDENCE_KEYS.has(key)) {
      collectEvidence(item, parts, seen, depth + 1);
    }
  }
};

const buildEvidence = (
  error: unknown,
  context: Record<string, unknown> = {},
): string => {
  const parts: string[] = [];
  const seen = new Set<object>();

  collectEvidence(error, parts, seen);
  collectEvidence(context, parts, seen);

  return parts.join('\n');
};

export const isNuxtChunkLoadError = (
  error: unknown,
  context: Record<string, unknown> = {},
): boolean => {
  const evidence = buildEvidence(error, context);

  return (
    CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(evidence)) &&
    (NUXT_ASSET_PATTERN.test(evidence) ||
      /dynamically imported module|chunkloaderror/i.test(evidence))
  );
};

export const recoverFromNuxtChunkLoadError = (
  error: unknown,
  context: Record<string, unknown> = {},
  options: RecoveryOptions = {},
): boolean => {
  if (typeof window === 'undefined') return false;
  if (!isNuxtChunkLoadError(error, context)) return false;

  const navigatorRef = options.navigator ?? window.navigator;
  if (navigatorRef && navigatorRef.onLine === false) return false;
  if (reloadScheduled) return true;

  const now = options.now?.() ?? Date.now();
  const cooldownMs = options.cooldownMs ?? DEFAULT_RECOVERY_COOLDOWN_MS;
  const storage = options.storage ?? window.sessionStorage;
  const storedLastReload = storage.getItem(RECOVERY_STORAGE_KEY);
  const lastReload =
    storedLastReload === null ? null : Number(storedLastReload);

  if (
    lastReload !== null &&
    Number.isFinite(lastReload) &&
    now - lastReload < cooldownMs
  ) {
    return false;
  }

  storage.setItem(RECOVERY_STORAGE_KEY, String(now));
  reloadScheduled = true;

  const reload =
    options.reload ?? (() => (options.location ?? window.location).reload());
  const schedule =
    options.schedule ??
    ((callback: () => void) => window.setTimeout(callback, 50));

  schedule(reload);

  return true;
};

export const resetNuxtChunkLoadRecoveryForTests = (): void => {
  reloadScheduled = false;
};
